import { useState, useEffect } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import DomainDetailsStyles from "screens/Settings/Domains/DomainDetails/DomainDetails.styles";
import {
  copyTextHandler,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import { getDomainDetails } from "screens/Settings/Domains/Domains.service";
import { domainDetailsTableHeader } from "screens/Settings/Domains/DomainsHelpers";
import copyIcon from "assets/icons1/copyIcon.svg";
import redInfoIcon from "assets/icons1/redInfoIcon.svg";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomTable from "global/components/CustomTable/CustomTable";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import need_help from "../../../../assets/icons1/need_help.svg";

interface CustomProps {
  domainStatus?: string;
  domainToBeDisplayed: string;
  isLoading: boolean;
  setIsLoading: Function;
  domainViewButtonHandler: Function;
  isStartVerification: boolean;
  setIsStartVerification: Function;
}

const DomainDetails = (props: CustomProps) => {
  const classes = DomainDetailsStyles;
  const [isLoading, setIsLoading] = useState(false);
  const [domainDetails, setDomainDetails] = useState<any>({});

  useEffect(() => {
    fetchDomainStatus();
  }, []);

  const fetchDomainStatus = async () => {
    try {
      props.setIsLoading(true);
      const response = await getDomainDetails(props.domainToBeDisplayed);
      props.setIsStartVerification(
        !!(
          response?.data?.custom_envelope?.valid && response?.data?.dkim?.valid
        )
      );
      const formattedRows = getFormattedDomainDetails(response);
      setDomainDetails(formattedRows);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      props.setIsLoading(false);
    }
  };

  const getFormattedDomainDetails = (rawDomainDetails: any) => {
    const formattedRowArray: any[] = [
      {
        ...getTableRow(
          rawDomainDetails.data.custom_envelope.host,
          rawDomainDetails.data.custom_envelope.type,
          rawDomainDetails.data.custom_envelope.required_value,
          rawDomainDetails.data.custom_envelope.valid
        ),
      },
      {
        ...getTableRow(
          rawDomainDetails.data.dkim.host,
          rawDomainDetails.data.dkim.type,
          rawDomainDetails.data.dkim.required_value,
          rawDomainDetails.data.dkim.valid
        ),
      },
    ];
    return formattedRowArray;
  };

  const getStatus = (status: number) => {
    if (status === 0) {
      return "Not Updated";
    }
    if (status === 1) {
      return "Updated";
    }
  };

  const getColors = {
    0: "#FF9436",
    1: "#47AA12",
  } as { [key: number]: string };

  const getTableRow = (
    hostName: string,
    type: String,
    requiredValue: string,
    status: number
  ) => {
    return {
      hostName: hostName,
      type: type,
      requiredValue: (
        <Box>
          <TextField
            disabled
            sx={classes.textField}
            variant="outlined"
            value={requiredValue}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img
                    src={copyIcon}
                    onClick={() => {
                      copyTextHandler(requiredValue);
                    }}
                    style={{ cursor: "pointer" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={classes.requiredValueDescriptionBox}>
            <img src={redInfoIcon} style={classes.redInfoIconStyles} />
            <Typography sx={classes.requiredValueInfo} variant="subtitle2">
              Copy the record and place it in your DNS settings
            </Typography>
          </Box>
        </Box>
      ),
      status: (
        <Typography
          sx={{
            color: getColors[status],
            display: "flex",
            alignItems: "center",
          }}
          variant="subtitle2"
        >
          <Box
            sx={{
              backgroundColor: getColors[status],
            }}
          />
          {getStatus(status)}
        </Typography>
      ),
    };
  };

  const getSubHeading = () => {
    return (
      <Box>
        <Typography variant="h4" color="text.primary">
          Authenticate Domain
        </Typography>
        <Typography variant="body2" color="text.primary">
          You will need to add the following records to complete the
          authentication process. Once added, hit the "Verify Status" button.{" "}
        </Typography>
      </Box>
    );
  };

  const getDomainDetailsTable = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Box>
          <CustomTable
            headers={domainDetailsTableHeader}
            rows={domainDetails}
            // emptyTableMessage={`Sorry, we don't have any details regarding ${props.domainToBeDisplayed} domain.`}
            isLoading={props.isLoading}
            showPagination={false}
          />
        </Box>
      </CenteredCardLayout>
    );
  };

  const getDomainDetailsPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        {getSubHeading()}
        {getDomainDetailsTable()}
        <Typography color={"#FF9436"} variant="body2">
          <span style={{ marginRight: 4 }}>
            <img src={need_help} />
          </span>
          Need help? Check our help article or contact our support. We’re here
          to help. 
        </Typography>
        <CustomLoader isLoading={isLoading} />
      </Stack>
    );
  };

  return getDomainDetailsPage();
};

export default DomainDetails;
